// app/lib/tracking/events.ts
import type {
  PurchaseEventData,
  AddToCartEventData,
  ViewItemEventData,
  BeginCheckoutEventData,
  ViewCartEventData,
  RemoveFromCartEventData,
  AddShippingInfoEventData,
  AddPaymentInfoEventData,
  EcommerceItem,
} from "./types";
import { trackGa4Event } from "./ga4";
import { trackAdsConversion } from "./google-ads";
import { fbqEvent } from "./facebook";
import { ttqEvent } from "./tiktok";
import { sendServerEvent, generateEventId } from "./server-event";

/**
 * Meta / TikTok / CAPI katalog kimliği.
 *
 * Merchant Center feed'indeki `g:id` = BEDEN seviyesindeki variant_id.
 * GA4'ün `item_id`'si ürün seviyesinde kalır (raporlar ürün bazlı okunuyor);
 * yalnızca reklam kanallarının katalog eşleşmesi varyanta iner.
 *
 * content_id yoksa item_id'ye düşer — event'i hiç göndermemektense ürün
 * seviyesinde eşleşmek yeğdir. Ayrıntı: types.ts → EcommerceItem.content_id
 */
const catalogId = (i: EcommerceItem): string =>
  String(i.content_id ?? i.item_id);

/**
 * Purchase — TÜM platformlara gönderir.
 * Browser + Server (CAPI) deduplication için event_id paylaşılır.
 */
export const trackPurchase = async (
  data: PurchaseEventData,
  adsConversionLabel?: string,
): Promise<void> => {
  const lockKey = `purchase_tracked_${data.transaction_id}`;
  if (typeof window !== "undefined") {
    if (sessionStorage.getItem(lockKey)) return;
    sessionStorage.setItem(lockKey, "1");
  }

  const currency = data.currency ?? "TRY";
  const eventId = generateEventId();

  trackGa4Event("purchase", {
    transaction_id: data.transaction_id,
    value: data.value,
    currency,
    tax: data.tax,
    shipping: data.shipping,
    items: data.items,
  });

  if (adsConversionLabel) {
    trackAdsConversion({
      conversionLabel: adsConversionLabel,
      value: data.value,
      currency,
      transactionId: data.transaction_id,
    });
  }

  fbqEvent(
    "Purchase",
    {
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      value: data.value,
      currency,
      num_items: data.items.reduce((sum, i) => sum + (i.quantity ?? 1), 0),
      contents: data.items.map((i) => ({
        id: catalogId(i),
        quantity: i.quantity ?? 1,
        item_price: i.price,
      })),
    },
    eventId,
  );

  ttqEvent("CompletePayment", {
    contents: data.items.map((i) => ({
      content_id: catalogId(i),
      content_name: i.item_name,
      price: i.price,
      quantity: i.quantity ?? 1,
    })),
    value: data.value,
    currency,
  });

  await sendServerEvent({
    event_name: "Purchase",
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    data: {
      currency,
      value: data.value,
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      num_items: data.items.reduce((sum, i) => sum + (i.quantity ?? 1), 0),
      contents: data.items.map((i) => ({
        id: catalogId(i),
        quantity: i.quantity ?? 1,
        item_price: i.price,
      })),
      order_id: data.transaction_id,
    },
    user_data: data.user,
  });
};

/**
 * Sepete ekleme — GA4 + Ads + FB + TikTok + CAPI.
 */
export const trackAddToCart = async (
  data: AddToCartEventData,
  adsConversionLabel?: string,
): Promise<void> => {
  const currency = data.currency ?? "TRY";
  const eventId = generateEventId();

  trackGa4Event("add_to_cart", {
    value: data.value,
    currency,
    items: data.items,
  });

  if (adsConversionLabel) {
    trackAdsConversion({
      conversionLabel: adsConversionLabel,
      value: data.value,
      currency,
    });
  }

  fbqEvent(
    "AddToCart",
    {
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      value: data.value,
      currency,
      contents: data.items.map((i) => ({
        id: catalogId(i),
        quantity: i.quantity ?? 1,
        item_price: i.price,
      })),
    },
    eventId,
  );

  ttqEvent("AddToCart", {
    contents: data.items.map((i) => ({
      content_id: catalogId(i),
      content_name: i.item_name,
      price: i.price,
      quantity: i.quantity ?? 1,
    })),
    value: data.value,
    currency,
  });

  await sendServerEvent({
    event_name: "AddToCart",
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    data: {
      currency,
      value: data.value,
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      contents: data.items.map((i) => ({
        id: catalogId(i),
        quantity: i.quantity ?? 1,
        item_price: i.price,
      })),
    },
  });
};

/**
 * Ürün görüntüleme — GA4 + Ads + FB + TikTok + CAPI.
 */
export const trackViewItem = async (
  data: ViewItemEventData,
  adsConversionLabel?: string,
): Promise<void> => {
  const currency = data.currency ?? "TRY";
  const eventId = generateEventId();

  trackGa4Event("view_item", {
    value: data.value,
    currency,
    items: data.items,
  });

  if (adsConversionLabel) {
    trackAdsConversion({
      conversionLabel: adsConversionLabel,
      value: data.value,
      currency,
    });
  }

  fbqEvent(
    "ViewContent",
    {
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      content_name: data.items[0]?.item_name,
      value: data.value,
      currency,
    },
    eventId,
  );

  ttqEvent("ViewContent", {
    contents: data.items.map((i) => ({
      content_id: catalogId(i),
      content_name: i.item_name,
      price: i.price,
      quantity: i.quantity ?? 1,
    })),
    value: data.value,
    currency,
  });

  await sendServerEvent({
    event_name: "ViewContent",
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    data: {
      currency,
      value: data.value,
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      content_name: data.items[0]?.item_name,
    },
  });
};

/**
 * Sepet görüntüleme — GA4 (view_cart).
 *
 * Sadece GA4'e gider: Meta ve TikTok'ta karşılık gelen standart bir event yok,
 * custom event uydurmak yerine tek kanaldan gönderiyoruz.
 *
 * Sayfa yüklemesine değil, sepet ekranının gerçekten görüntülenmesine bağlı
 * tetiklenmeli — client-side navigasyonda (ör. "Sepete Git") page load olmuyor
 * ve sepet verisi mount anında henüz gelmemiş olabiliyor.
 */
export const trackViewCart = (data: ViewCartEventData): void => {
  trackGa4Event("view_cart", {
    value: data.value,
    currency: data.currency ?? "TRY",
    items: data.items,
  });
};

/**
 * Sepetten çıkarma — GA4 (remove_from_cart).
 *
 * view_cart gibi sadece GA4'e gider; Meta/TikTok'ta standart karşılığı yok.
 * Silme işlemi başarıyla tamamlandıktan sonra tek sefer gönderilir — çağrı
 * cart.hook içindeki removeItem'da, yani her silme için tam bir push.
 */
export const trackRemoveFromCart = (data: RemoveFromCartEventData): void => {
  trackGa4Event("remove_from_cart", {
    value: data.value,
    currency: data.currency ?? "TRY",
    items: data.items,
  });
};

/**
 * Checkout başlangıcı — GA4 + Ads + FB + TikTok + CAPI.
 */
export const trackBeginCheckout = async (
  data: BeginCheckoutEventData,
  adsConversionLabel?: string,
): Promise<void> => {
  const currency = data.currency ?? "TRY";
  const eventId = generateEventId();

  trackGa4Event("begin_checkout", {
    value: data.value,
    currency,
    items: data.items,
  });

  if (adsConversionLabel) {
    trackAdsConversion({
      conversionLabel: adsConversionLabel,
      value: data.value,
      currency,
    });
  }

  fbqEvent(
    "InitiateCheckout",
    {
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      value: data.value,
      currency,
      num_items: data.items.reduce((sum, i) => sum + (i.quantity ?? 1), 0),
    },
    eventId,
  );

  ttqEvent("InitiateCheckout", {
    contents: data.items.map((i) => ({
      content_id: catalogId(i),
      content_name: i.item_name,
      price: i.price,
      quantity: i.quantity ?? 1,
    })),
    value: data.value,
    currency,
  });

  await sendServerEvent({
    event_name: "InitiateCheckout",
    event_id: eventId,
    event_source_url: typeof window !== "undefined" ? window.location.href : "",
    data: {
      currency,
      value: data.value,
      content_ids: data.items.map((i) => catalogId(i)),
      content_type: "product",
      num_items: data.items.reduce((sum, i) => sum + (i.quantity ?? 1), 0),
    },
  });
};

/**
 * Teslimat adresi eklendi/seçildi — GA4 (add_shipping_info).
 *
 * view_cart/remove_from_cart gibi sadece GA4'e gider; Meta/TikTok'ta
 * standart karşılığı yok. checkout/page.tsx'te adres bilgisi hazır olunca
 * (varsayılan veya kullanıcı seçimi) oturum başına bir kez gönderilir.
 */
export const trackAddShippingInfo = (data: AddShippingInfoEventData): void => {
  trackGa4Event("add_shipping_info", {
    value: data.value,
    currency: data.currency ?? "TRY",
    items: data.items,
  });
};

/**
 * Ödeme yöntemi eklendi/seçildi — GA4 (add_payment_info).
 *
 * view_cart/remove_from_cart gibi sadece GA4'e gider; Meta/TikTok'ta
 * standart karşılığı yok. checkout/page.tsx'te ödeme yöntemi hazır olunca
 * (varsayılan veya kullanıcı seçimi) oturum başına bir kez gönderilir.
 */
export const trackAddPaymentInfo = (data: AddPaymentInfoEventData): void => {
  trackGa4Event("add_payment_info", {
    value: data.value,
    currency: data.currency ?? "TRY",
    payment_type: data.payment_type,
    items: data.items,
  });
};

export const trackSearch = (searchTerm: string): void => {
  trackGa4Event("search", { search_term: searchTerm });
  fbqEvent("Search", { search_string: searchTerm });
  ttqEvent("Search", { query: searchTerm });
};

export const trackSignUp = (method: string = "email"): void => {
  trackGa4Event("sign_up", { method });
  fbqEvent("CompleteRegistration", { registration_method: method });
  ttqEvent("CompleteRegistration", { registration_method: method });
};

export const trackLogin = (method: string = "email"): void => {
  trackGa4Event("login", { method });
};
